// src/components/dashboard/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import {
    Container, Grid, Paper, Typography, Button,
    CircularProgress, Box, Divider, useTheme, useMediaQuery,
    Card, CardContent, Chip
} from '@mui/material';
import { Add as AddIcon, TrendingUp, Assessment, Schedule } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import RiskSummary from './RiskSummary';
import RiskTrends from './RiskTrends';
import { getAssessments } from '../../services/assessmentService';

const Dashboard = () => {
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        fetchAssessments();
    }, []);

    const fetchAssessments = async () => {
        try {
            setLoading(true);
            const data = await getAssessments();
            setAssessments(data);
        } catch (err) {
            setError('Failed to load assessments. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '50vh'
            }}>
                <CircularProgress size={isMobile ? 40 : 60} />
            </Container>
        );
    }

    return (
        <Container
            maxWidth="lg"
            sx={{
                py: { xs: 2, sm: 3 },
                px: { xs: 1, sm: 2, md: 3 }
            }}
        >
            {/* Header Section */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: 2
            }}>
                <Typography
                    variant={isMobile ? "h5" : "h4"}
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary'
                    }}
                >
                    Risk Assessment Dashboard
                </Typography>
                <Button
                    component={Link}
                    to="/new-assessment"
                    variant="contained"
                    size={isMobile ? "large" : "medium"}
                    startIcon={<AddIcon />}
                    sx={{
                        minWidth: { xs: '100%', sm: 200 },
                        py: { xs: 1.5, sm: 1 },
                        borderRadius: 2,
                        boxShadow: 2
                    }}
                >
                    New Assessment
                </Button>
            </Box>

            {/* Error Message */}
            {error && (
                <Paper
                    elevation={1}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        mb: 3,
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="body1">{error}</Typography>
                </Paper>
            )}

            <Grid container spacing={{ xs: 2, sm: 3 }}>
                {/* Main Charts Section */}
                <Grid item xs={12} lg={8}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2, sm: 3 },
                            borderRadius: 3,
                            height: '100%',
                            minHeight: { xs: 350, sm: 400 }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Risk Trends
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        {assessments.length > 0 ? (
                            <Box sx={{
                                height: { xs: 280, sm: 320, md: 350 },
                                width: '100%'
                            }}>
                                <RiskTrends assessments={assessments} />
                            </Box>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: { xs: 250, sm: 300 },
                                textAlign: 'center',
                                color: 'text.secondary'
                            }}>
                                <Assessment sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                <Typography variant={isMobile ? "body1" : "h6"} sx={{ mb: 1 }}>
                                    No Data Available
                                </Typography>
                                <Typography variant="body2">
                                    Create your first assessment to see trends and analytics
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Risk Summary Section */}
                <Grid item xs={12} lg={4}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2, sm: 3 },
                            borderRadius: 3,
                            height: '100%',
                            minHeight: { xs: 250, sm: 350 }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Assessment sx={{ mr: 1, color: 'secondary.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Risk Summary
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <RiskSummary assessments={assessments} />
                    </Paper>
                </Grid>

                {/* Recent Assessments Section */}
                <Grid item xs={12}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2, sm: 3 },
                            borderRadius: 3
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Schedule sx={{ mr: 1, color: 'info.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Recent Assessments
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        {assessments.length === 0 ? (
                            <Card
                                variant="outlined"
                                sx={{
                                    textAlign: 'center',
                                    py: { xs: 4, sm: 6 },
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    borderColor: 'divider'
                                }}
                            >
                                <CardContent>
                                    <Assessment sx={{
                                        fontSize: { xs: 48, sm: 64 },
                                        mb: 2,
                                        color: 'text.secondary',
                                        opacity: 0.5
                                    }} />
                                    <Typography
                                        variant={isMobile ? "h6" : "h5"}
                                        sx={{ mb: 2, fontWeight: 500 }}
                                    >
                                        No Assessments Yet
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
                                    >
                                        Get started by creating your first risk assessment.
                                        Our AI-powered system will help you identify and analyze potential risks.
                                    </Typography>
                                    <Button
                                        component={Link}
                                        to="/new-assessment"
                                        variant="contained"
                                        size={isMobile ? "large" : "medium"}
                                        startIcon={<AddIcon />}
                                        sx={{
                                            minWidth: { xs: 200, sm: 'auto' },
                                            py: { xs: 1.5, sm: 1 },
                                            borderRadius: 2
                                        }}
                                    >
                                        Create First Assessment
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <>
                                <Grid container spacing={{ xs: 1, sm: 2 }}>
                                    {assessments.slice(0, 5).map((assessment) => (
                                        <Grid item xs={12} key={assessment.id}>
                                            <Card
                                                variant="outlined"
                                                component={Link}
                                                to={`/assessment/${assessment.id}`}
                                                sx={{
                                                    textDecoration: 'none',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        boxShadow: 4,
                                                        transform: 'translateY(-1px)',
                                                        borderColor: 'primary.main'
                                                    },
                                                    borderRadius: 2
                                                }}
                                            >
                                                <CardContent sx={{
                                                    p: { xs: 2, sm: 2.5 },
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    alignItems: { xs: 'stretch', sm: 'center' },
                                                    gap: { xs: 1.5, sm: 2 }
                                                }}>
                                                    {/* Assessment Info */}
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant={isMobile ? "subtitle1" : "h6"}
                                                            sx={{
                                                                fontWeight: 600,
                                                                mb: 0.5,
                                                                color: 'text.primary',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical'
                                                            }}
                                                        >
                                                            {assessment.title}
                                                        </Typography>

                                                        <Box sx={{
                                                            display: 'flex',
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                                            gap: { xs: 0.5, sm: 1 },
                                                            mb: 1
                                                        }}>
                                                            <Chip
                                                                label={assessment.domain.charAt(0).toUpperCase() + assessment.domain.slice(1)}
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                {new Date(assessment.created_at).toLocaleDateString()}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Risk Score */}
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: { xs: 'center', sm: 'flex-end' },
                                                        flexShrink: 0
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: getRiskColor(assessment.risk_score),
                                                            color: 'white',
                                                            borderRadius: '50%',
                                                            width: { xs: 44, sm: 50 },
                                                            height: { xs: 44, sm: 50 },
                                                            boxShadow: 2
                                                        }}>
                                                            <Typography
                                                                variant={isMobile ? "body1" : "h6"}
                                                                fontWeight="bold"
                                                            >
                                                                {assessment.risk_score.toFixed(1)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* View All Button */}
                                {assessments.length > 5 && (
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mt: 3
                                    }}>
                                        <Button
                                            variant="outlined"
                                            component={Link}
                                            to="/history"
                                            size={isMobile ? "large" : "medium"}
                                            sx={{
                                                minWidth: { xs: 200, sm: 'auto' },
                                                py: { xs: 1.5, sm: 1 },
                                                borderRadius: 2
                                            }}
                                        >
                                            View All Assessments ({assessments.length})
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

// Helper function to get color based on risk score
const getRiskColor = (score) => {
    if (score >= 7) return '#d32f2f'; // High risk - red
    if (score >= 4) return '#f57c00'; // Medium risk - orange
    return '#388e3c'; // Low risk - green
};

export default Dashboard;